import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Pencil,
  Plus,
  Share2,
  Trash2,
  FileText,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { useDelete, useGetOne, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "../layout/ThemeToggle";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SellerAvatar } from "./SellerAvatar";
import { StarRating } from "./StarRating";
import {
  type Meeting,
  type MeetingAttendee,
  formatMeetingDate,
  formatMeetingTime,
  formatMoneyMx,
  initials,
  linesToList,
  meetingScore,
  sentimentLabel,
  findSectorLabel,
  findStageLabel,
  sourceLabel,
} from "./meetingsUtils";

/**
 * MeetingShow — detalle P3 Bento Spatial.
 *
 * Layout:
 *   ┌─ back-bar (← Juntas / company-breadcrumb / ThemeToggle / Compartir / Crear tarea)
 *   ├─ detail-grid (2fr / 1fr)
 *   │   ├─ Columna principal:
 *   │   │     hero (top: logo + h2 + meta + score pill)
 *   │   │     audio-hero (gradient amarillo + play + waveform + tiempo)
 *   │   │     bento (2 cols: Temas / Pain points / Próximos pasos full / Transcripción full)
 *   │   └─ Columna lateral (sticky):
 *   │         deal-card · empresa · asistentes · captura
 */
export function MeetingShow() {
  const params = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { companySectors, dealStages, currency } = useConfigurationContext();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: meeting, isPending, error } = useGetOne<Meeting>(
    "meetings_summary",
    { id: params.id! },
    { enabled: !!params.id },
  );

  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const handleDelete = () => {
    deleteOne(
      "meetings",
      { id: params.id!, previousData: meeting },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ["meetings"] });
          await queryClient.invalidateQueries({
            queryKey: ["meetings_summary"],
          });
          notify("Junta eliminada", { type: "success" });
          navigate("/meetings");
        },
        onError: (e: any) => notify(e?.message ?? "Error al eliminar", { type: "warning" }),
      },
    );
  };

  if (isPending) {
    return <DetailSkeleton />;
  }
  if (error || !meeting) {
    return (
      <div className="p-7">
        <Link to="/meetings" className="musco-btn musco-btn-g inline-flex">
          <ArrowLeft size={14} /> Volver a juntas
        </Link>
        <div
          className="mt-6 rounded-2xl p-8 text-sm"
          style={{
            background: "var(--musco-panel)",
            color: "var(--musco-muted)",
            boxShadow: "var(--musco-shadow)",
          }}
        >
          No se pudo cargar la junta. {error?.message ?? ""}
        </div>
      </div>
    );
  }

  const topics = linesToList(meeting.topics);
  const issues = linesToList((meeting.raw_extraction as any)?.issues || "");
  const nextSteps = linesToList(meeting.next_steps);
  const transcriptLines = parseTranscript(meeting.transcript);

  const score = meetingScore(meeting);
  const sent = sentimentLabel(score);

  const stageLabel = findStageLabel(dealStages, meeting.deal_stage);
  const sectorLbl = findSectorLabel(companySectors, meeting.company_sector);

  const logo = (
    meeting.company_logo?.title?.charAt(0) ||
    meeting.company_name?.charAt(0) ||
    "?"
  ).toUpperCase();

  return (
    <div className="px-7 pt-6 pb-7 overflow-auto h-full">
      {/* Confirm delete dialog */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar esta junta?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Se borrará la junta, sus
              asistentes y la referencia a los archivos (los archivos en
              storage quedan disponibles vía URL si conoces el link).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => {
                setConfirmDelete(false);
                handleDelete();
              }}
              style={{
                background: "var(--musco-hot)",
                color: "white",
              }}
            >
              Sí, eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Back bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Link to="/meetings" className="musco-btn musco-btn-g">
          <ArrowLeft size={14} /> Juntas
        </Link>
        <span className="text-sm" style={{ color: "var(--musco-muted)" }}>
          / {meeting.company_name}
        </span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <ThemeToggle />
          <button type="button" className="musco-btn musco-btn-g">
            <Share2 size={14} /> Compartir
          </button>
          <Link
            to={`/meetings/${params.id}`}
            className="musco-btn musco-btn-g"
          >
            <Pencil size={14} /> Editar
          </Link>
          <button
            type="button"
            className="musco-btn musco-btn-g"
            onClick={() => setConfirmDelete(true)}
            disabled={isDeleting}
            style={{ color: "var(--musco-hot)" }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          <Link
            to="/tasks/create"
            state={{ record: { contact_id: meeting.attendees?.[0]?.id } }}
            className="musco-btn musco-btn-a"
          >
            <Plus size={14} /> Crear tarea
          </Link>
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 items-start">
        {/* MAIN column */}
        <div>
          <div className="musco-hero">
            <HeroHeader
              logo={logo}
              meeting={meeting}
              sector={sectorLbl}
              score={score}
              sentimentLabel={sent.label}
            />

            {/* Hero compacto — solo identidad de la junta (sin audio player) */}
            <MeetingMetaStrip meeting={meeting} />

            {/* Bento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-4">
              {topics.length > 0 ? (
                <div className="musco-b-card">
                  <h4>Temas tratados</h4>
                  <ul>
                    {topics.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {issues.length > 0 ? (
                <div className="musco-b-card warn">
                  <h4>Pain points</h4>
                  <ul>
                    {issues.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {nextSteps.length > 0 ? (
                <div className="musco-b-card" style={{ gridColumn: "1 / -1" }}>
                  <h4>Próximos pasos</h4>
                  <div>
                    {nextSteps.map((s, i) => (
                      <NextStepRow
                        key={i}
                        text={s}
                        seller={meeting.sales_id}
                        sellerName={meeting.sales_name ?? ""}
                        sellerAvatar={meeting.sales_avatar?.src}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {transcriptLines.length > 0 ? (
                <div className="musco-b-card" style={{ gridColumn: "1 / -1" }}>
                  <h4>Transcripción</h4>
                  <div>
                    {transcriptLines.map((l, i) => (
                      <div
                        key={i}
                        className="grid"
                        style={{
                          gridTemplateColumns: "54px 1fr",
                          gap: 14,
                          padding: "10px 0",
                          borderTop:
                            i === 0
                              ? "none"
                              : "1px solid var(--musco-line)",
                        }}
                      >
                        <div
                          className="text-xs tnums"
                          style={{ color: "var(--musco-muted)" }}
                        >
                          {l.t}
                        </div>
                        <div>
                          <div className="font-semibold text-[13px] mb-0.5">
                            {l.speaker}
                          </div>
                          <div className="text-[13.5px] leading-[1.55]">
                            {l.text}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : meeting.transcript ? (
                <div className="musco-b-card" style={{ gridColumn: "1 / -1" }}>
                  <h4>Transcripción</h4>
                  <div className="text-[13.5px] leading-[1.55] whitespace-pre-wrap">
                    {meeting.transcript}
                  </div>
                </div>
              ) : null}

              {meeting.summary ? (
                <div className="musco-b-card" style={{ gridColumn: "1 / -1" }}>
                  <h4>Resumen</h4>
                  <div className="text-[13.5px] leading-[1.55] whitespace-pre-wrap">
                    {meeting.summary}
                  </div>
                </div>
              ) : null}

              {meeting.agreements ? (
                <div className="musco-b-card" style={{ gridColumn: "1 / -1" }}>
                  <h4>Acuerdos</h4>
                  <div className="text-[13.5px] leading-[1.55] whitespace-pre-wrap">
                    {meeting.agreements}
                  </div>
                </div>
              ) : null}

              {Array.isArray((meeting as any).attachments) &&
              (meeting as any).attachments.length > 0 ? (
                <div className="musco-b-card" style={{ gridColumn: "1 / -1" }}>
                  <h4>Archivos adjuntos</h4>
                  <div className="flex flex-col gap-2">
                    {(meeting as any).attachments.map((att: any, i: number) => (
                      <a
                        key={i}
                        href={att.src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 py-1.5 px-3 rounded-md text-sm hover:underline"
                        style={{
                          background: "var(--musco-panel)",
                          color: "var(--musco-text)",
                        }}
                      >
                        <FileText size={14} style={{ color: "var(--musco-muted)" }} />
                        <span className="truncate flex-1">{att.title ?? "Archivo"}</span>
                        <span
                          className="text-xs"
                          style={{ color: "var(--musco-muted)" }}
                        >
                          {att.type ?? ""}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* SIDE column (sticky) */}
        <div className="flex flex-col gap-3.5 lg:sticky lg:top-6">
          {meeting.deal_id ? (
            <Link
              to={`/deals/${meeting.deal_id}/show`}
              className="musco-deal-card no-underline"
              style={{ display: "block", color: "white" }}
            >
              <div className="l">Oportunidad ligada</div>
              <div className="nm">{meeting.deal_name}</div>
              <div className="am">
                {formatMoneyMx(meeting.deal_amount ?? 0, currency)}
              </div>
              <div className="st">● {stageLabel}</div>
            </Link>
          ) : null}

          <div className="musco-side-card">
            <h4>Empresa</h4>
            <div className="kv">
              <span className="k">Sector</span>
              <span>{sectorLbl}</span>
            </div>
            {meeting.company_city ? (
              <div className="kv">
                <span className="k">Ciudad</span>
                <span>{meeting.company_city}</span>
              </div>
            ) : null}
            {meeting.location ? (
              <div className="kv">
                <span className="k">Lugar de la junta</span>
                <span className="text-right">{meeting.location}</span>
              </div>
            ) : null}
            <div className="kv">
              <span className="k">Ver</span>
              <Link
                to={`/companies/${meeting.company_id}/show`}
                style={{ color: "var(--musco-accent2)", textDecoration: "none", fontWeight: 600 }}
              >
                Abrir ficha →
              </Link>
            </div>
          </div>

          <div className="musco-side-card">
            <h4>Asistentes ({meeting.attendee_count ?? 0})</h4>
            {(meeting.attendees ?? []).length === 0 ? (
              <p className="text-sm" style={{ color: "var(--musco-muted)" }}>
                Aún no se registraron asistentes. Edita la junta para agregarlos.
              </p>
            ) : null}
            {(meeting.attendees ?? []).map((a, i) => (
              <Attendee key={i} attendee={a} />
            ))}
            <div
              className="flex items-center gap-2.5 py-1.5 text-sm"
              style={{
                paddingTop: 10,
                marginTop: 6,
                borderTop: "1px solid var(--musco-line)",
              }}
            >
              <SellerAvatar
                id={meeting.sales_id}
                name={meeting.sales_name}
                size={28}
                imageUrl={meeting.sales_avatar?.src}
              />
              <div>
                <div className="font-semibold">{meeting.sales_name}</div>
                <div className="text-xs" style={{ color: "var(--musco-muted)" }}>
                  Vendedor Musco
                </div>
              </div>
            </div>
          </div>

          <div className="musco-side-card">
            <h4>Captura</h4>
            <div className="kv">
              <span className="k">Canal</span>
              <span>{sourceLabel(meeting.source)}</span>
            </div>
            <div className="kv">
              <span className="k">Idioma</span>
              <span>{meeting.language === "es" ? "Español" : meeting.language ?? "—"}</span>
            </div>
            {meeting.audio_url ? (
              <div className="kv">
                <span className="k">Audio</span>
                <a
                  href={meeting.audio_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--musco-accent2)", textDecoration: "none", fontWeight: 600 }}
                >
                  Descargar →
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroHeader({
  logo,
  meeting,
  sector,
  score,
  sentimentLabel,
}: {
  logo: string;
  meeting: Meeting;
  sector: string;
  score: number;
  sentimentLabel: string;
}) {
  return (
    <div className="flex gap-4 items-start mb-4.5">
      <div
        className="grid place-items-center font-bold text-xl flex-shrink-0"
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: "var(--musco-panel2)",
        }}
      >
        {logo}
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="m-0 text-2xl font-bold tracking-[-0.025em]">
          Junta · {meeting.company_name}
        </h2>
        <div
          className="mt-1.5 text-sm flex gap-3 flex-wrap"
          style={{ color: "var(--musco-muted)" }}
        >
          <span className="flex items-center gap-1">
            <Calendar size={13} /> {formatMeetingDate(meeting.occurred_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={13} /> {formatMeetingTime(meeting.occurred_at)}
          </span>
          {(meeting.location || meeting.company_city) ? (
            <span className="flex items-center gap-1">
              <MapPin size={13} />
              {meeting.location || meeting.company_city}
            </span>
          ) : null}
          <span>· {sector}</span>
        </div>
      </div>
      <span
        className="musco-pill"
        style={
          score > 80
            ? { background: "#0a0a0a", color: "var(--musco-accent)" }
            : { background: "transparent", color: "var(--musco-muted)", border: "1px solid var(--musco-line)" }
        }
      >
        ★ {sentimentLabel} · {score}
      </span>
    </div>
  );
}

/**
 * MeetingMetaStrip — hero card que reemplaza al ex-AudioPlayer.
 *
 * Ahora ocupa el mismo espacio visual pero muestra:
 *   - Calificación en estrellas (1-5) protagónica al centro
 *   - Lugar, canal de captura, idioma, asistentes en una tira de metadata
 *   - Si hay audio_url externo (registrado vía API), aparece como link
 *
 * El usuario lee de un vistazo qué tan productiva fue la junta y dónde fue.
 */
function MeetingMetaStrip({ meeting }: { meeting: Meeting }) {
  const hasRating = typeof meeting.rating === "number" && meeting.rating > 0;
  return (
    <div className="musco-audio-hero">
      {/* Stars hero — protagónico */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div
            className="text-xs uppercase tracking-wider font-semibold mb-1.5"
            style={{ color: "rgba(10,10,10,0.7)" }}
          >
            Calificación de la junta
          </div>
          <div className="flex items-center gap-3">
            <StarRating
              value={meeting.rating ?? 0}
              size={32}
              readOnly
              color="#0a0a0a"
            />
            <span
              className="text-2xl font-bold tracking-tight tnums"
              style={{ color: "#0a0a0a" }}
            >
              {hasRating ? `${meeting.rating}/5` : "Sin calificar"}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-xs uppercase tracking-wider font-semibold mb-1.5"
            style={{ color: "rgba(10,10,10,0.7)" }}
          >
            Capturada por
          </div>
          <div className="text-sm font-semibold" style={{ color: "#0a0a0a" }}>
            {meeting.sales_name}
          </div>
          <div className="text-xs" style={{ color: "rgba(10,10,10,0.6)" }}>
            vía {sourceLabel(meeting.source)}
          </div>
        </div>
      </div>

      {/* Metadata strip inferior */}
      <div
        className="mt-4 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm"
        style={{ borderTop: "1px solid rgba(10,10,10,0.18)" }}
      >
        <MetaItem label="Fecha" value={formatMeetingDate(meeting.occurred_at)} />
        <MetaItem label="Hora" value={formatMeetingTime(meeting.occurred_at)} />
        <MetaItem
          label="Lugar"
          value={meeting.location || meeting.company_city || "—"}
        />
        <MetaItem
          label="Asistentes"
          value={String(meeting.attendee_count ?? 0)}
        />
      </div>

      {meeting.audio_url ? (
        <div className="mt-3 text-xs">
          <a
            href={meeting.audio_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
            style={{ color: "#0a0a0a" }}
          >
            🔗 Audio adjunto (link externo, registrado vía API)
          </a>
        </div>
      ) : null}
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-wider font-semibold"
        style={{ color: "rgba(10,10,10,0.6)" }}
      >
        {label}
      </div>
      <div
        className="text-sm font-semibold truncate tnums"
        style={{ color: "#0a0a0a" }}
        title={value}
      >
        {value}
      </div>
    </div>
  );
}

function NextStepRow({
  text,
  seller,
  sellerName,
  sellerAvatar,
}: {
  text: string;
  seller: number | string;
  sellerName: string;
  sellerAvatar?: string;
}) {
  // Soporta sintaxis "[Quién] qué - YYYY-MM-DD"
  const dueMatch = text.match(/-?\s*(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2})\s*$/);
  const due = dueMatch?.[1];
  const without = due ? text.replace(dueMatch![0], "").trim() : text;
  return (
    <div className="musco-step">
      <SellerAvatar
        id={seller}
        name={sellerName}
        size={26}
        imageUrl={sellerAvatar}
      />
      <div className="text-[13.5px] leading-[1.5]">{without}</div>
      <div className="due">{due ?? ""}</div>
    </div>
  );
}

function Attendee({ attendee }: { attendee: MeetingAttendee }) {
  return (
    <Link
      to={`/contacts/${attendee.id}/show`}
      className="flex items-center gap-2.5 py-1.5 text-sm hover:opacity-80 transition-opacity no-underline"
      style={{ color: "var(--musco-text)" }}
    >
      <div
        className="grid place-items-center"
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "var(--musco-panel2)",
          fontWeight: 600,
          fontSize: 11,
        }}
      >
        {initials(attendee.name)}
      </div>
      <div className="min-w-0">
        <div className="font-semibold truncate">{attendee.name}</div>
        <div className="text-xs truncate" style={{ color: "var(--musco-muted)" }}>
          {attendee.role || attendee.title || "—"}
        </div>
      </div>
    </Link>
  );
}

function parseTranscript(raw?: string | null): { t: string; speaker: string; text: string }[] {
  if (!raw) return [];
  // Formato preferido: "00:00:12 — Speaker: text"
  // Aceptamos también: "Speaker: text" (sin tiempo)
  const lines = raw.split(/\r?\n+/).filter(Boolean);
  return lines.map((line, i) => {
    const m = line.match(/^(?:(\d{1,2}:\d{2}(?::\d{2})?)\s*[—\-:]\s*)?([^:]{2,40}):\s*(.+)$/);
    if (m) {
      return { t: m[1] ?? `${(i + 1) * 30}s`, speaker: m[2].trim(), text: m[3].trim() };
    }
    return { t: "", speaker: "Conversación", text: line.trim() };
  });
}

function DetailSkeleton() {
  return (
    <div className="p-7">
      <div className="flex gap-3 mb-4">
        <div
          className="musco-btn musco-btn-g"
          style={{ opacity: 0.6, pointerEvents: "none" }}
        >
          ← Juntas
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <div
          className="musco-hero"
          style={{ minHeight: 480, opacity: 0.5 }}
        />
        <div className="flex flex-col gap-3.5">
          <div className="musco-deal-card" style={{ minHeight: 140, opacity: 0.5 }} />
          <div className="musco-side-card" style={{ minHeight: 120, opacity: 0.5 }} />
          <div className="musco-side-card" style={{ minHeight: 160, opacity: 0.5 }} />
        </div>
      </div>
    </div>
  );
}
